import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  Card, 
  CardContent, 
  CircularProgress,
  Button,
  Alert,
  Tooltip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db, auth } from '../firebase';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { 
  Chart as ChartJS, 
  CategoryScale,  
  LinearScale, 
  PointElement, 
  LineElement, 
  BarElement,
  Title, 
  Tooltip as ChartTooltip, 
  Legend, 
  ArcElement 
} from 'chart.js';
import { Bar, Pie } from 'react-chartjs-2';
import BarChartIcon from '@mui/icons-material/BarChart';
import TimelineIcon from '@mui/icons-material/Timeline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SaveIcon from '@mui/icons-material/Save';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import * as amplitude from '@amplitude/analytics-browser';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  ChartTooltip,
  Legend,
  ArcElement
);

const Reports = () => {
  const [loading, setLoading] = useState(true);
  const [recordings, setRecordings] = useState([]);
  const [summaryMetrics, setSummaryMetrics] = useState({
    totalRecordings: 0,
    totalDuration: 0,
    totalStorage: 0,
    timeSaved: 0,
    avgDuration: 0,
    successRate: 0
  });
  const navigate = useNavigate();

  const chartColors = {
    primary: 'rgba(156, 39, 176, 0.7)',
    secondary: 'rgba(255, 87, 34, 0.7)',
    tertiary: 'rgba(33, 150, 243, 0.7)',
    quaternary: 'rgba(76, 175, 80, 0.7)',
    light: 'rgba(200, 200, 200, 0.3)',
    background: 'rgba(156, 39, 176, 0.1)'
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const user = auth.currentUser;
        if (!user) return;
        amplitude.track('Reports Page Viewed', { userId: user.uid });
        
        const q = query(
          collection(db, "recordings"),
          where("userId", "==", user.uid),
          orderBy("createdAt", "desc")
        );
        
        const querySnapshot = await getDocs(q);
        const recordingsData = [];
        querySnapshot.forEach((doc) => {
          recordingsData.push({ id: doc.id, ...doc.data() });
        });
        setRecordings(recordingsData);
        calculateMetrics(recordingsData);
      } catch (error) {
        console.error("Error fetching recordings data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const calculateMetrics = (data) => {
    if (!data || data.length === 0) {
      setSummaryMetrics({
        totalRecordings: 0,
        totalDuration: 0,
        totalStorage: 0,
        timeSaved: 0,
        avgDuration: 0,
        successRate: 0
      });
      return;
    }
    const totalRecordings = data.length;
    const totalDuration = data.reduce((acc, rec) => acc + (rec.duration || 0), 0);
    const totalStorage = data.reduce((acc, rec) => acc + (Number(rec.size) || 0), 0);
    const timeSaved = totalDuration * 5 - totalDuration;
    const avgDuration = totalDuration / totalRecordings;
    const successfulRecordings = data.filter(rec => rec.status === "transcribed").length;
    const successRate = (successfulRecordings / totalRecordings) * 100;
    setSummaryMetrics({
      totalRecordings,
      totalDuration,
      totalStorage,
      timeSaved,
      avgDuration,
      successRate
    });
  };

  const formatDuration = (seconds, includeSeconds = false) => {
    if (!seconds) return '0ժ';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    let result = '';
    if (hours > 0) result += `${hours}ժ `;
    if (minutes > 0) result += `${minutes}ր `;
    if (includeSeconds && (remainingSeconds > 0 || (hours === 0 && minutes === 0))) result += `${remainingSeconds}վրկ`;
    return result.trim() || '0ժ';
  };

  const formatDateForArmenian = (date) => {
    if (!date) return '';
    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();
    return `${day < 10 ? '0' + day : day}.${month < 10 ? '0' + month : month}.${year}`;
  };

  const formatStorage = (mb) => {
    const num = Number(mb);
    if (isNaN(num)) return '0 MB';
    if (num < 1) {
      return `${(num * 1000).toFixed(0)} KB`;
    } else if (num < 1000) {
      return `${num.toFixed(2)} MB`;
    } else {
      return `${(num / 1000).toFixed(2)} GB`;
    }
  };

  const prepareDurationChartData = () => {
    if (recordings.length === 0) return null;
    const months = {};
    const monthNames = ['Հունվար', 'Փետրվար', 'Մարտ', 'Ապրիլ', 'Մայիս', 'Հունիս', 
                        'Հուլիս', 'Օգոստոս', 'Սեպտեմբեր', 'Հոկտեմբեր', 'Նոյեմբեր', 'Դեկտեմբեր'];
    recordings.forEach(rec => {
      if (rec.createdAt && rec.duration) {
        const date = rec.createdAt.toDate();
        const monthKey = `${date.getFullYear()}-${date.getMonth()}`;
        const monthLabel = `${monthNames[date.getMonth()]} ${date.getFullYear()}`;
        if (!months[monthKey]) {
          months[monthKey] = { label: monthLabel, duration: 0, count: 0 };
        }
        months[monthKey].duration += rec.duration || 0;
        months[monthKey].count += 1;
      }
    });
    const sortedMonths = Object.keys(months).sort().map(key => months[key]);
    return {
      labels: sortedMonths.map(month => month.label),
      datasets: [
        {
          label: 'Ձայնագրության տևողություն (րոպե)',
          data: sortedMonths.map(month => month.duration / 60),
          backgroundColor: chartColors.primary,
          borderColor: chartColors.primary,
          borderWidth: 1
        }
      ]
    };
  };

  const prepareSourceChartData = () => {
    if (recordings.length === 0) return null;
    const sources = {};
    recordings.forEach(rec => {
      const source = rec.source || 'unknown';
      if (!sources[source]) {
        sources[source] = 0;
      }
      sources[source] += 1;
    });
    const sourceLabels = {
      'upload': 'Վերբեռնում',
      'recording': 'Ձայնագրություն',
      'unknown': 'Անհայտ'
    };
    return {
      labels: Object.keys(sources).map(key => sourceLabels[key] || key),
      datasets: [
        {
          data: Object.values(sources),
          backgroundColor: [
            chartColors.primary,
            chartColors.secondary,
            chartColors.tertiary
          ],
          borderWidth: 1
        }
      ]
    };
  };

  const getDateRangeText = () => {
    if (recordings.length === 0) return 'Ամբողջ ժամանակահատվածը';
    
    let earliestDate = new Date();
    recordings.forEach(rec => {
      if (rec.createdAt) {
        const date = rec.createdAt.toDate();
        if (date < earliestDate) {
          earliestDate = date;
        }
      }
    });
    
    const today = new Date();
    return `${formatDateForArmenian(earliestDate)} - ${formatDateForArmenian(today)}`;
  };

  const emptyPageContent = (
    <Box sx={{ textAlign: 'center', p: 3 }}>
      <Paper sx={{ 
        p: 4, 
        backgroundColor: chartColors.background,
        backgroundImage: 'radial-gradient(circle, rgba(156, 39, 176, 0.1) 0%, rgba(156, 39, 176, 0.05) 100%)'
      }}>
        <AssessmentIcon sx={{ fontSize: 80, color: 'primary.main', mb: 2 }} />
        <Typography variant="h5" component="h2" gutterBottom>
          Դուք դեռ չունեք վիճակագրական տվյալներ
        </Typography>
        <Typography variant="body1" paragraph>
          Այս էջում դուք կտեսնեք ձեր ձայնագրությունների վիճակագրությունը:
        </Typography>
        <Button 
          variant="contained" 
          color="primary" 
          size="large"
          onClick={() => {
            amplitude.track('Empty Reports Page Button Clicked', { userId: auth.currentUser?.uid });
            navigate("/record");
          }}
        >
          Կատարել առաջին ձայնագրությունը
        </Button>
        <Grid container spacing={3} sx={{ mt: 4, opacity: 0.4 }}>
          <Grid item xs={12} md={6}>
            <Box sx={{ height: 200 }}>
              <Bar 
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: { legend: { display: false } }
                }}
                data={{
                  labels: ['Հունվար', 'Փետրվար', 'Մարտ', 'Ապրիլ', 'Մայիս'],
                  datasets: [{
                    data: [12, 19, 8, 15, 10],
                    backgroundColor: chartColors.primary
                  }]
                }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ height: 200 }}>
              <Pie 
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: { legend: { display: false } }
                }}
                data={{
                  labels: ['Վերբեռնում', 'Ձայնագրություն'],
                  datasets: [{
                    data: [60, 40],
                    backgroundColor: [chartColors.primary, chartColors.secondary]
                  }]
                }}
              />
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );

  const reportsContent = (
    <Box>
      <Grid container spacing={3} alignItems="center" sx={{ mb: 2 }}>
        <Grid item xs={12}>
          <Typography variant="h4" component="h1">
            Օգտագործման վիճակագրություն
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            Ժամանակահատված: {getDateRangeText()}
          </Typography>
        </Grid>
      </Grid>
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            height: '100%', 
            backgroundColor: chartColors.background,
            transition: 'all 0.3s ease',
            '&:hover': { boxShadow: '0 4px 20px rgba(156, 39, 176, 0.15)' }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BarChartIcon sx={{ color: 'primary.main', mr: 1 }} />
                <Typography variant="h6" component="div">
                  Վերծանումներ
                </Typography>
              </Box>
              <Typography variant="h4" component="div">
                {summaryMetrics.totalRecordings}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            height: '100%', 
            backgroundColor: chartColors.background,
            transition: 'all 0.3s ease',
            '&:hover': { boxShadow: '0 4px 20px rgba(156, 39, 176, 0.15)' }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AccessTimeIcon sx={{ color: 'primary.main', mr: 1 }} />
                <Typography variant="h6" component="div">
                  Ընդհանուր տևողություն
                </Typography>
              </Box>
              <Typography variant="h4" component="div">
                {formatDuration(summaryMetrics.totalDuration, false)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Միջինում {formatDuration(summaryMetrics.avgDuration, true)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            height: '100%', 
            backgroundColor: chartColors.background,
            transition: 'all 0.3s ease',
            '&:hover': { boxShadow: '0 4px 20px rgba(156, 39, 176, 0.15)' }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TimelineIcon sx={{ color: 'primary.main', mr: 1 }} />
                <Typography variant="h6" component="div">
                  Խնայված ժամանակ*
                </Typography>
              </Box>
              <Typography variant="h4" component="div">
                {formatDuration(summaryMetrics.timeSaved, false)}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Ինչպես է հաշվարկվել
                </Typography>
                <Tooltip title={
                  <Typography style={{ fontSize: '0.95rem' }}>
                    Մարդու կողմից վերծանումը միջինում պահանջում է 5x ավելի ժամանակ
                  </Typography>
                }>
                  <InfoIcon fontSize="small" sx={{ ml: 0.5, color: 'text.secondary' }} />
                </Tooltip>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            height: '100%', 
            backgroundColor: chartColors.background,
            transition: 'all 0.3s ease',
            '&:hover': { boxShadow: '0 4px 20px rgba(156, 39, 176, 0.15)' }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SaveIcon sx={{ color: 'primary.main', mr: 1 }} />
                <Typography variant="h6" component="div">
                  Հիշողություն
                </Typography>
              </Box>
              <Typography variant="h4" component="div">
                {formatStorage(summaryMetrics.totalStorage)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {recordings.length} ֆայլերում
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ 
            p: 3, 
            transition: 'all 0.3s ease',
            '&:hover': { boxShadow: '0 4px 20px rgba(156, 39, 176, 0.15)' }
          }}>
            <Typography variant="h6" gutterBottom>
              Ձայնագրության տևողությունը ըստ ամսների
            </Typography>
            <Box sx={{ height: 300 }}>
              {prepareDurationChartData() ? (
                <Bar 
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    scales: {
                      y: {
                        beginAtZero: true,
                        title: {
                          display: true,
                          text: 'Րոպե'
                        }
                      }
                    }
                  }}
                  data={prepareDurationChartData()}
                />
              ) : (
                <Typography variant="body2" sx={{ textAlign: 'center', pt: 8 }}>
                  Տվյալներ չկան
                </Typography>
              )}
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ 
            p: 3, 
            transition: 'all 0.3s ease',
            '&:hover': { boxShadow: '0 4px 20px rgba(156, 39, 176, 0.15)' }
          }}>
            <Typography variant="h6" gutterBottom>
              Վերծանումների աղբյուրները
            </Typography>
            <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {prepareSourceChartData() ? (
                <Pie 
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'bottom',
                        labels: {
                          padding: 20,
                          boxWidth: 15
                        }
                      }
                    }
                  }}
                  data={prepareSourceChartData()}
                />
              ) : (
                <Typography variant="body2" sx={{ textAlign: 'center' }}>
                  Տվյալներ չկան
                </Typography>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
      <Alert 
        severity="warning" 
        icon={<WarningIcon />} 
        sx={{ mt: 3, backgroundColor: 'rgba(255, 244, 229, 0.9)', fontSize: '0.9rem' }}
      >
        <Typography variant="body2">
          Եթե դուք ջնջել եք ձեր վերծանումները, այստեղ ցուցադրվող օգտագործումը կարող է չհամապատասխանել ձեր վճարված օգտագործմանը:
        </Typography>
      </Alert>
    </Box>
  );

  return (
    <Box sx={{ px: { xs: 1, sm: 2 }, py: 3 }}>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      ) : (
        recordings.length > 0 ? reportsContent : emptyPageContent
      )}
    </Box>
  );
};

export default Reports;